import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Tag,
  Space,
  Typography,
  Tooltip,
  Button,
  Modal,
  Descriptions,
  Divider,
  Dropdown,
} from "antd";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomButton from "../../../components/CustomButton/Button";
import { formatDate } from "../../../helpers/helpers";
import { safeStringSort, safeDateSort } from "../../../helpers/sortHelpers";
import { getBusinessSuppliers } from "../../../redux/slices/BusSlice";
import AddSupplierModal from "./AddSupplierModal";
import EditSupplierModal from "./EditSupplierModal";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import CustomInput from "../../../components/CustomInput/Input";

const { Text } = Typography;

const SuppliersList = ({ suppliersData, orgId }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state?.business || {});

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState(null);

  // Auto-fetch if suppliersData not supplied directly
  useEffect(() => {
    if (orgId && !suppliersData) {
      dispatch(getBusinessSuppliers(orgId));
    }
  }, [dispatch, orgId, suppliersData]);

  const rawList = useMemo(() => {
    return Array.isArray(suppliersData) ? suppliersData : [];
  }, [suppliersData]);

  // Client-side filtering
  const filteredData = useMemo(() => {
    let list = rawList;

    if (statusFilter !== "ALL") {
      list = list.filter(
        (item) =>
          (item.status || "").toUpperCase() === statusFilter.toUpperCase()
      );
    }

    if (searchText?.trim()) {
      const q = searchText.toLowerCase().trim();
      list = list.filter(
        (item) =>
          item.supplierName?.toLowerCase().includes(q) ||
          item.supplierId?.toLowerCase().includes(q) ||
          item.pocName?.toLowerCase().includes(q) ||
          item.pocEmail?.toLowerCase().includes(q) ||
          item.pocMobile?.toLowerCase().includes(q) ||
          item.pan?.toLowerCase().includes(q) ||
          item.gstin?.toLowerCase().includes(q) ||
          item.bankName?.toLowerCase().includes(q) ||
          item.ifsc?.toLowerCase().includes(q)
      );
    }

    return list;
  }, [rawList, statusFilter, searchText]);

  const handleView = (record) => {
    setSelectedSupplier(record);
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setSupplierToEdit(record);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSupplier(null);
  };

  const handleRefresh = useCallback(() => {
    if (orgId) {
      dispatch(getBusinessSuppliers(orgId));
    }
  }, [dispatch, orgId]);

  const columns = [
    {
      title: "Supplier Name",
      dataIndex: "supplierName",
      key: "supplierName",
      width: 220,
      sorter: (a, b) => safeStringSort(a?.supplierName, b?.supplierName),
      render: (text, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{text || "-"}</Text>
          <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
            {record.supplierId || "-"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Contact Person",
      dataIndex: "pocName",
      key: "pocName",
      width: 180,
      sorter: (a, b) => safeStringSort(a?.pocName, b?.pocName),
      render: (text, record) => (
        <Space direction="vertical" size={2}>
          <Text>{text || "-"}</Text>
          <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
            {record.pocMobile || "-"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "pocEmail",
      key: "pocEmail",
      width: 200,
      render: (text) => text || "-",
    },
    {
      title: "Tax / KYC",
      key: "taxKyc",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text style={{ fontSize: 13 }}>{record.pan || "-"}</Text>
          <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
            {record.gstin || "-"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Bank Details",
      key: "bankDetails",
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.isBankVerified ? (
            <Tag color="success">Verified</Tag>
          ) : (
            <Tag color="default">Unverified</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      sorter: (a, b) => safeStringSort(a?.status, b?.status),
      render: (status) => {
        if (status === "ACTIVE") {
          return <Tag color="success">Active</Tag>;
        }
        if (status === "INACTIVE") {
          return <Tag color="error">Inactive</Tag>;
        }
        return <Tag color="warning">{status || "Pending"}</Tag>;
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 160,
      render: (text) => {
        if (!text) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={text}>
            <Text
              type="secondary"
              ellipsis
              style={{ maxWidth: 140, display: "block" }}
            >
              {text}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Onboarded Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      sorter: (a, b) => safeDateSort(a?.createdAt, b?.createdAt),
      render: (date) => (date ? formatDate(date) : "-"),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      align: "center",
      render: (_, record) => {
        const items = [
          {
            key: "view",
            label: "View",
            onClick: () => handleView(record),
          },
          {
            key: "edit",
            label: "Edit",
            onClick: () => handleEdit(record),
          },
        ];

        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              aria-label="Actions"
              icon={<MoreOutlined />}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="suppliers-list-section" style={{ padding: "8px 0" }}>
      {/* Header controls: Search, Status Filter, Count, Add Button */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16 }}
        gutter={[16, 16]}
      >
        <Col>
          <Space size={12}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Suppliers ({filteredData.length})
            </Typography.Title>
            <CustomInput
              placeholder="Search Supplier..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 220 }}
              allowClear
            />
            <CustomSelect
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              style={{ width: 130 }}
              options={[
                { value: "ALL", label: "All Status" },
                { value: "ACTIVE", label: "Active" },
                { value: "INACTIVE", label: "Inactive" },
              ]}
            />
          </Space>
        </Col>
        <Col>
          <CustomButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddModalOpen(true)}
            style={{ background: "#294DD8" }}
          >
            Add New Supplier
          </CustomButton>
        </Col>
      </Row>

      <CustomTable
        rowKey="supplierId"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
      />

      {/* Supplier Detailed View Modal */}
      <Modal
        title={
          <Space>
            <span>Supplier Details</span>
            {selectedSupplier?.status === "ACTIVE" ? (
              <Tag color="success">Active</Tag>
            ) : selectedSupplier?.status === "INACTIVE" ? (
              <Tag color="error">Inactive</Tag>
            ) : (
              <Tag color="warning">{selectedSupplier?.status || "Pending"}</Tag>
            )}
          </Space>
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseModal}>
            Close
          </Button>,
        ]}
        width={720}
      >
        {selectedSupplier && (
          <div style={{ marginTop: 16 }}>
            <Descriptions title="Basic & KYC Information" bordered size="small" column={2}>
              <Descriptions.Item label="Supplier Name">
                <strong>{selectedSupplier.supplierName || "-"}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Supplier ID">
                {selectedSupplier.supplierId || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="PAN">
                {selectedSupplier.pan || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="GSTIN">
                {selectedSupplier.gstin || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Onboarded Date" span={2}>
                {selectedSupplier.createdAt ? formatDate(selectedSupplier.createdAt) : "-"}
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: "16px 0" }} />

            <Descriptions title="Contact Person (POC) Details" bordered size="small" column={2}>
              <Descriptions.Item label="POC Name">
                {selectedSupplier.pocName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Mobile Number">
                {selectedSupplier.pocMobile || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>
                {selectedSupplier.pocEmail || "-"}
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: "16px 0" }} />

            <Descriptions title="Bank & Settlement Details" bordered size="small" column={2}>
              <Descriptions.Item label="Account Holder Name">
                {selectedSupplier.accHolderName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Account Number">
                {selectedSupplier.accNum || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Bank Name">
                {selectedSupplier.bankName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Branch Name">
                {selectedSupplier.branchName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="IFSC Code">
                {selectedSupplier.ifsc || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Bank Verification">
                {selectedSupplier.isBankVerified ? (
                  <Tag color="success">Verified</Tag>
                ) : (
                  <Tag color="default">Unverified</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>

            {selectedSupplier.remarks && (
              <>
                <Divider style={{ margin: "16px 0" }} />
                <Descriptions title="Remarks / Notes" bordered size="small" column={1}>
                  <Descriptions.Item label="Remarks">
                    {selectedSupplier.remarks}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Add Supplier Modal */}
      <AddSupplierModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleRefresh}
        orgId={orgId}
        existingSuppliers={rawList}
      />

      {/* Edit Supplier Modal */}
      <EditSupplierModal
        open={isEditModalOpen}
        supplier={supplierToEdit}
        onClose={() => {
          setIsEditModalOpen(false);
          setSupplierToEdit(null);
        }}
        onSuccess={handleRefresh}
        orgId={orgId}
        existingSuppliers={rawList}
      />
    </div>
  );
};

export default SuppliersList;
